import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Modal,
  MenuItem,
  Alert,
  IconButton,
  InputAdornment,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Visibility, VisibilityOff, Delete } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";

import { usePostUser } from "../../../hooks/panelUsers/usePostUser";
import { useFetchSections } from "../../../hooks/useGetSections";
import { DocumentAutocomplete } from "./DocumentAutocomplete";

export const ModalAddUser = ({ open, onClose, token, refresh }) => {
  const [user, setUser] = useState("");
  const [email, setEmail] = useState("");
  const [rol, setRol] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [expandedDocId, setExpandedDocId] = useState(null);

  const [documentID, setDocumentID] = useState(null);
  const [documents, setDocuments] = useState([]);

  const { addUser, loading } = usePostUser(token);
  const { sections } = useFetchSections(token, documentID?.id);

  useEffect(() => {
    if (!open) {
      setUser("");
      setEmail("");
      setRol("");
      setPassword("");
      setError("");
      setDocumentID(null);
      setDocuments([]);
    }
  }, [open]);

  useEffect(() => {
    if (documentID && sections.length > 0) {
      const exists = documents.some((doc) => doc.document_id === documentID.id);
      if (!exists) {
        const newDoc = {
          document_id: documentID.id,
          name: documentID.label,
          sections: sections.map((sec) => ({
            section_id: sec.ID_SECCION,
            name: sec.NOMBRE_SECCION,
            active: false,
          })),
        };
        setDocuments((prev) => [...prev, newDoc]);
      }
      setDocumentID(null);
    }
  }, [documentID, sections]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (docId, sectionId) => {
    setDocuments((prev) =>
      prev.map((doc) => {
        if (doc.document_id !== docId) return doc;
        return {
          ...doc,
          sections: doc.sections.map((sec) =>
            sec.section_id === sectionId ? { ...sec, active: !sec.active } : sec
          ),
        };
      })
    );
  };

  const handleRemoveDocument = (docId) => {
    setDocuments((prev) => prev.filter((doc) => doc.document_id !== docId));
  };

  const handleAddUser = async () => {
    if (
      !user ||
      !email ||
      !password ||
      !rol ||
      (rol === "usuario" && documents.length === 0)
    ) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    const payload = {
      user_name: user,
      email,
      password,
      rol,
      documents: documents.map((doc) => ({
        document_id: doc.document_id,
        sections: doc.sections
          .filter((sec) => sec.active)
          .map((sec) => sec.section_id),
      })),
    };

    try {
      await addUser(payload);
      onClose();
      refresh();
    } catch (err) {
      setError("Hubo un error al agregar el usuario. Inténtalo de nuevo.");
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" component="h2" mb={2}>
          AGREGAR USUARIO
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              margin="normal"
              label="Nombre"
              value={user}
              onChange={(e) => setUser(e.target.value)}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Box>

          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              margin="normal"
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Rol"
              select
              value={rol}
              onChange={(e) => setRol(e.target.value)}
            >
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="editor">Editor</MenuItem>
              <MenuItem value="usuario">Usuario / Cliente</MenuItem>
            </TextField>
          </Box>

          {rol === "usuario" && (
            <>
              <Divider sx={{ my: 4 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  Asignar un documento
                </Typography>
              </Divider>

              <DocumentAutocomplete
                token={token}
                documentID={documentID}
                setDocumentID={setDocumentID}
                assignedDocuments={documents.map((doc) => ({
                  ID_DOCUMENTO: doc.document_id,
                  NOMBRE_DOCUMENTO: doc.name,
                }))}
              />

              <Box sx={{ mt: 2, height: 300, overflowY: "auto" }}>
                {documents.map((doc) => (
                  <Accordion
                    key={doc.document_id}
                    expanded={expandedDocId === doc.document_id}
                    onChange={() =>
                      setExpandedDocId((prev) =>
                        prev === doc.document_id ? null : doc.document_id
                      )
                    }
                    sx={{
                      mb: 1,
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ bgcolor: "#e5e5e5" }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveDocument(doc.document_id);
                          }}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                        <Typography fontWeight={500}>{doc.name}</Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                    >
                      {doc.sections.map((section) => (
                        <Chip
                          key={section.section_id}
                          label={section.name}
                          onClick={() =>
                            toggleSection(doc.document_id, section.section_id)
                          }
                          icon={<AddCircleRoundedIcon />}
                          variant={section.active ? "filled" : "outlined"}
                          color={section.active ? "primary" : "default"}
                          sx={{ fontSize: "12px" }}
                        />
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </>
          )}

          <Button
            fullWidth
            sx={{ mt: 3 }}
            variant="contained"
            color="primary"
            onClick={handleAddUser}
            disabled={loading}
          >
            {loading ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
